'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Heart, ShoppingCart, Check, Star } from 'lucide-react';
import { Product, ProductVariant } from '@/data/mockData';
import { useCommerce } from '@/context/CommerceContext';

interface ProductCardProps {
  product: Product;
  index?: number;
  customImage?: string;
  customDisplayName?: string;
  className?: string;
}

export function calculateUnitPrice(price: number, weightStr: string): string {
  const lower = (weightStr || '').toLowerCase().trim();
  if (lower.includes('kg')) {
    const kg = parseFloat(lower.replace('kg', '')) || 1;
    const perKg = price / kg;
    return `₹ ${perKg.toFixed(2)}* per 1 kg`;
  }
  if (lower.includes('g')) {
    const g = parseFloat(lower.replace('g', '')) || 250;
    const perKg = (price / g) * 1000;
    return `₹ ${perKg.toFixed(2)}* per 1 kg`;
  }
  if (lower.includes('l') || lower.includes('litre')) {
    const l = parseFloat(lower.replace(/[^\d.]/g, '')) || 1;
    const perL = price / l;
    return `₹ ${perL.toFixed(2)}* per 1 l`;
  }
  return `₹ ${price.toFixed(2)}* per pack`;
}

export default function ProductCard({
  product,
  index = 0,
  customImage,
  customDisplayName,
  className = '',
}: ProductCardProps) {
  const { addToCart } = useCommerce();

  // Deduplicate variants by weight to ensure pristine unique options
  const uniqueVariants = React.useMemo(() => {
    if (!product.variants || product.variants.length === 0) return [];
    const seen = new Set<string>();
    return product.variants.filter((v) => {
      const w = v.weight?.trim();
      if (!w || seen.has(w)) return false;
      seen.add(w);
      return true;
    });
  }, [product.variants]);

  const [selectedWeight, setSelectedWeight] = useState<string>(
    uniqueVariants[0]?.weight || product.variants?.[0]?.weight || '250g'
  );
  const [isWishlisted, setIsWishlisted] = useState<boolean>(false);
  const [isAdded, setIsAdded] = useState<boolean>(false);

  const activeVariant: ProductVariant =
    uniqueVariants.find((v) => v.weight === selectedWeight) ||
    uniqueVariants[0] ||
    product.variants?.[0] || {
      weight: '250g',
      price: 150,
      stock: 50,
      sku: `${product.id}-default`,
    };

  const displayImage = customImage || product.image;
  const displayName = customDisplayName || `${product.name} ${selectedWeight}`;

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    addToCart({
      productId: product.id,
      name: displayName,
      tamilName: product.tamilName,
      image: displayImage,
      weight: selectedWeight,
      unitPrice: activeVariant.price,
      quantity: 1,
    });

    setIsAdded(true);
    setTimeout(() => {
      setIsAdded(false);
    }, 1400);
  };

  const toggleWishlist = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsWishlisted((prev) => !prev);
  };

  return (
    <div className={`flex flex-col group select-none isolate relative ${className}`}>
      {/* 1. Main Image Container: Full-bleed product image with rounded corners */}
      <div
        className="relative w-full aspect-square rounded-[18px] sm:rounded-[20px] overflow-hidden bg-[#FAF9F5] border border-neutral-200/60 transition-all duration-300 shadow-2xs group-hover:shadow-md"
      >
        <Link
          href={`/product/${product.slug}`}
          className="relative w-full h-full block overflow-hidden p-2 sm:p-2.5"
        >
          <Image
            src={displayImage || '/images/pouch_kai_murukku.png'}
            alt={product.name || 'Elavarasi Traditional Delicacy'}
            fill
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
            unoptimized
            className="object-contain w-full h-full transform transition-transform duration-500 ease-out group-hover:scale-105"
          />
        </Link>

        {/* Top-Right Heart / Wishlist Icon */}
        <button
          type="button"
          onClick={toggleWishlist}
          aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
          className="absolute top-2.5 right-2.5 z-10 w-7 h-7 rounded-full flex items-center justify-center transition-transform hover:scale-110 active:scale-95 cursor-pointer bg-white/70 backdrop-blur-xs shadow-2xs"
        >
          <Heart
            className={`w-4 h-4 sm:w-4.5 sm:h-4.5 transition-colors duration-200 ${
              isWishlisted
                ? 'fill-[#E11D48] text-[#E11D48]'
                : 'text-neutral-500 stroke-[2] hover:text-red-500'
            }`}
          />
        </button>

        {/* Inverted Curved Cutout Notch in Bottom-Right Corner */}
        <div className="absolute bottom-0 right-0 z-[5] w-[60px] h-[60px] sm:w-[64px] sm:h-[64px] pointer-events-none">
          <svg
            className="w-full h-full fill-white"
            viewBox="0 0 72 72"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M 73,0 A 16,16 0 0,1 56,16 L 36,16 A 20,20 0 0,0 16,36 L 16,56 A 16,16 0 0,1 0,73 L 73,73 Z" />
          </svg>
        </div>

        {/* Bottom-Right Floating Green Circular Add-to-Cart Button sitting inside the curved notch */}
        <button
          type="button"
          onClick={handleAddToCart}
          aria-label="Add to cart"
          className={`absolute bottom-1.5 right-1.5 z-10 w-8.5 h-8.5 sm:w-9 sm:h-9 rounded-full flex items-center justify-center shadow-md transition-all duration-200 cursor-pointer ${
            isAdded
              ? 'bg-[#15803D] scale-105 text-white'
              : 'bg-[#52A05B] hover:bg-[#43874B] text-white hover:scale-105 active:scale-95'
          }`}
        >
          {isAdded ? (
            <Check className="w-4 h-4 stroke-[2.5]" />
          ) : (
            <ShoppingCart className="w-4 h-4 stroke-[2.2]" />
          )}
        </button>
      </div>

      {/* 2. Below Image Details Area */}
      <div className="pt-2 space-y-0.5">
        {/* Rating Row: Small Star + Rating (Count) */}
        <div className="flex items-center space-x-1 text-[11px] text-neutral-600">
          <Star className="w-3 h-3 fill-[#F5A623] text-[#F5A623] shrink-0" />
          <span className="font-semibold text-neutral-800">
            {product.rating ? product.rating.toFixed(1) : '4.8'}
          </span>
          <span className="text-neutral-500">
            ({product.reviewsCount || 742})
          </span>
        </div>

        {/* Product Title */}
        <Link href={`/product/${product.slug}`} className="block">
          <h3 className="font-normal text-[14px] sm:text-[15px] text-neutral-800 leading-snug line-clamp-2 hover:text-[#52A05B] transition-colors">
            {product.name}
          </h3>
        </Link>

        {/* Weight Selector Chips (if multiple variants available) */}
        {uniqueVariants && uniqueVariants.length > 1 && (
          <div className="flex items-center space-x-1 pt-0.5">
            {uniqueVariants.map((v, vIdx) => {
              const isSelected = selectedWeight === v.weight;
              return (
                <button
                  key={`${product.id}-${v.weight}-${vIdx}`}
                  type="button"
                  onClick={() => setSelectedWeight(v.weight)}
                  className={`px-1.5 py-0.5 text-[10px] font-bold rounded transition-all cursor-pointer ${
                    isSelected
                      ? 'bg-neutral-900 text-white'
                      : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-600'
                  }`}
                >
                  {v.weight}
                </button>
              );
            })}
          </div>
        )}

        {/* Price & Unit Price Calculation */}
        <div className="pt-0.5">
          <div className="font-semibold text-base sm:text-[17px] text-neutral-900 leading-tight">
            ₹ {activeVariant.price.toFixed(2)}
          </div>
          <div className="text-[11px] text-neutral-400 font-normal mt-0.5">
            {calculateUnitPrice(activeVariant.price, selectedWeight)}
          </div>
        </div>
      </div>
    </div>
  );
}
